import { ArrowRight, Loader2, MapPin, Wallet } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CompanyLogo from '../../../components/CompanyLogo';
import {
  fetchHomepageFeaturedJobs,
  type HomepageFeaturedJob,
} from '../../../services/homepageJobsService';
import { trackCTA } from '../../../utils/metaPixel';

interface HomeJobMatchPreviewProps {
  onNavigate?: (view: string) => void;
}

const CAROUSEL_JOB_COUNT = 12;
const MARQUEE_SPEED_PX_PER_SEC = 48;

function JobMarqueeCard({
  job,
  t,
}: {
  job: HomepageFeaturedJob;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <article className="home-job-match-preview__card">
      <CompanyLogo
        url={job.companyLogoUrl}
        company={job.company}
        imgClassName="home-job-match-preview__logo-img"
        fallbackClassName="home-job-match-preview__logo-fallback"
        alt={t('home.jobMatchPreview.companyLogoAlt', { company: job.company })}
      />

      <div className="home-job-match-preview__card-body">
        <div className="home-job-match-preview__card-topline">
          <h3 className="home-job-match-preview__job-title">{job.title}</h3>
          {job.matchScore != null ? (
            <span className="home-job-match-preview__match-pill">{job.matchScore}%</span>
          ) : (
            <span className="home-job-match-preview__source-badge">
              {job.sourceChannel}
            </span>
          )}
        </div>

        <p className="home-job-match-preview__company">{job.company}</p>

        <div className="home-job-match-preview__meta">
          <span className="home-job-match-preview__meta-row">
            <MapPin className="home-job-match-preview__meta-icon" aria-hidden />
            {job.location}
          </span>
          <span className="home-job-match-preview__meta-row home-job-match-preview__meta-row--salary">
            <Wallet className="home-job-match-preview__meta-icon" aria-hidden />
            {job.salaryLabel}
          </span>
        </div>
      </div>
    </article>
  );
}

function JobMarqueeGroup({
  jobs,
  t,
  groupId,
  ariaHidden = false,
}: {
  jobs: HomepageFeaturedJob[];
  t: (key: string, options?: Record<string, unknown>) => string;
  groupId: string;
  ariaHidden?: boolean;
}) {
  return (
    <div className="home-job-match-preview__marquee-group" aria-hidden={ariaHidden || undefined}>
      {jobs.map((job) => (
        <span key={`${groupId}-${job.id}`} className="home-job-match-preview__marquee-item">
          <JobMarqueeCard job={job} t={t} />
        </span>
      ))}
    </div>
  );
}

/** JS-driven marquee — works even when global reduced-motion CSS neuters @keyframes. */
function useJobMarqueeMotion(
  trackRef: React.RefObject<HTMLDivElement | null>,
  marqueeRef: React.RefObject<HTMLDivElement | null>,
  active: boolean,
) {
  useEffect(() => {
    if (!active) return;

    const track = trackRef.current;
    const marquee = marqueeRef.current;
    if (!track) return;

    const firstGroup = track.querySelector(
      '.home-job-match-preview__marquee-group',
    ) as HTMLElement | null;
    if (!firstGroup) return;

    let offset = 0;
    let paused = false;
    let raf = 0;
    let lastTime = 0;

    const pause = () => {
      paused = true;
    };
    const resume = () => {
      paused = false;
      lastTime = 0;
    };

    marquee?.addEventListener('mouseenter', pause);
    marquee?.addEventListener('mouseleave', resume);
    marquee?.addEventListener('focusin', pause);
    marquee?.addEventListener('focusout', resume);

    const step = (time: number) => {
      if (!lastTime) lastTime = time;
      const dt = Math.min((time - lastTime) / 1000, 0.05);
      lastTime = time;

      if (!paused) {
        const groupWidth = firstGroup.offsetWidth;
        if (groupWidth > 0) {
          offset += MARQUEE_SPEED_PX_PER_SEC * dt;
          if (offset >= groupWidth) offset -= groupWidth;
          track.style.transform = `translate3d(-${offset}px, 0, 0)`;
        }
      }

      raf = window.requestAnimationFrame(step);
    };

    track.classList.add('home-job-match-preview__marquee-track--js');
    raf = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(raf);
      marquee?.removeEventListener('mouseenter', pause);
      marquee?.removeEventListener('mouseleave', resume);
      marquee?.removeEventListener('focusin', pause);
      marquee?.removeEventListener('focusout', resume);
      track.classList.remove('home-job-match-preview__marquee-track--js');
      track.style.transform = '';
    };
  }, [active, trackRef, marqueeRef]);
}

const HomeJobMatchPreview: React.FC<HomeJobMatchPreviewProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<HomepageFeaturedJob[]>([]);
  const [locationLabel, setLocationLabel] = useState('India');
  const [loading, setLoading] = useState(true);
  const [hasPersonalScores, setHasPersonalScores] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchHomepageFeaturedJobs(CAROUSEL_JOB_COUNT)
      .then((result) => {
        if (cancelled) return;
        setJobs(result.jobs);
        setLocationLabel(result.locationLabel);
        setHasPersonalScores(result.jobs.some((j) => j.matchScore != null));
      })
      .catch(() => {
        if (!cancelled) setJobs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useJobMarqueeMotion(trackRef, marqueeRef, !loading && jobs.length > 0);

  const handleCTA = () => {
    trackCTA('job_match_preview_signup');
    onNavigate?.('signup');
  };

  if (!loading && jobs.length === 0) {
    return null;
  }

  return (
    <section className="home-job-match-preview">
      <div className="home-job-match-preview__container">
        <div className="home-job-match-preview__header">
          <h2 className="home-job-match-preview__title">
            {t('home.jobMatchPreview.title')}
          </h2>
          <p className="home-job-match-preview__subtitle">
            {hasPersonalScores
              ? t('home.jobMatchPreview.subtitlePersonal', { location: locationLabel })
              : t('home.jobMatchPreview.subtitle', { location: locationLabel })}
          </p>
          <p className="home-job-match-preview__trust t-meta">
            {t(
              'home.jobMatchPreview.trustNote',
              'You choose which roles to apply to. GoGrabJob prepares tailored materials; you review and submit.',
            )}
          </p>
        </div>

        {loading ? (
          <div className="home-job-match-preview__loading" aria-busy="true">
            <Loader2
              className="w-6 h-6 animate-spin"
              style={{ color: 'var(--ds-marketing-accent, #5F8568)' }}
            />
            <span className="t-meta">{t('home.jobMatchPreview.loading')}</span>
          </div>
        ) : (
          <div ref={marqueeRef} className="home-job-match-preview__marquee">
            <div className="home-job-match-preview__marquee-viewport">
              <div
                ref={trackRef}
                className="home-job-match-preview__marquee-track"
                role="region"
                aria-label={t('home.jobMatchPreview.carouselLabel')}
              >
                <JobMarqueeGroup jobs={jobs} t={t} groupId="a" />
                <JobMarqueeGroup jobs={jobs} t={t} groupId="b" ariaHidden />
              </div>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={handleCTA}
          className="home-marketing-cta home-job-match-preview__cta group font-sans touch-manipulation"
          disabled={loading}
        >
          {t('home.jobMatchPreview.cta')}
          <ArrowRight
            className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
            aria-hidden
          />
        </button>
      </div>
    </section>
  );
};

export default React.memo(HomeJobMatchPreview);
